import { useEffect, useRef, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { updatePosition, clearPositions, setGpsStatus } from '../store/slices/vehiclesSlice'
import { Backoff } from '../utils/reconnect'
import { addAlert } from '../store/slices/alertsSlice'
import { setDemoState, resetDemo, DemoState } from '../store/slices/demoSlice'
import { setBoarded, setParentMessage } from '../store/slices/parentSlice'
import { store } from '../store'
import { eventConcernsParent, DemoEventMsg } from '../utils/eventTargeting'
import { wsUrl } from '../config/runtime'
import toast from 'react-hot-toast'

function eventConcernsUser(msg: DemoEventMsg): boolean {
  return eventConcernsParent(msg, store.getState().auth.user, store.getState().parent)
}

const WS_URL = wsUrl()

/**
 * Connexion WebSocket active (singleton) — permet à la page Démo « conducteur »
 * de diffuser des messages sans ouvrir une seconde connexion.
 */
let activeWs: WebSocket | null = null

/** Diffuse un message, relayé par le serveur à tous les clients connectés. */
export function broadcastBus(payload: Record<string, unknown>) {
  // readyState === 1 (OPEN) — valeur numérique robuste (mock de test compris).
  if (activeWs && activeWs.readyState === 1) {
    activeWs.send(
      JSON.stringify({ type: 'BUS_BROADCAST', vehicleId: payload.vehicleId ?? null, payload }),
    )
  }
}

/**
 * Envoie un message brut sur la connexion WebSocket active (sans enveloppe
 * BUS_BROADCAST). Utilisé par le suivi conducteur (DRIVER_POSITION / DRIVER_EVENT).
 * @returns true si le message a pu être envoyé.
 */
export function sendTracking(message: object): boolean {
  if (activeWs && activeWs.readyState === 1) {
    activeWs.send(JSON.stringify(message))
    return true
  }
  return false
}

export function useTrackingWebSocket() {
  const dispatch = useDispatch()
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Reconnexion robuste : back-off exponentiel borné (Phase 2).
  const backoffRef = useRef(new Backoff({ baseMs: 1000, maxMs: 30000 }))

  const connect = useCallback(() => {
    const token = localStorage.getItem('token')
    const url = token ? `${WS_URL}?token=${token}` : WS_URL

    const ws = new WebSocket(url)
    wsRef.current = ws
    activeWs = ws

    ws.onopen = () => {
      console.log('WebSocket connecté')
      // Abonnement à tous les véhicules (dashboard)
      ws.send(JSON.stringify({ type: 'SUBSCRIBE_ALL' }))
      backoffRef.current.reset()
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
    }

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data)

        // Le simulateur a été arrêté : on purge la carte et la démo.
        if (message.type === 'SIMULATOR_STOPPED') {
          dispatch(clearPositions())
          dispatch(resetDemo())
          toast('Simulateur arrêté — suivi temps réel réinitialisé', { icon: '🛑' })
          return
        }

        if (message.type === 'VEHICLE_POSITION') {
          dispatch(updatePosition({
            vehicleId: message.vehicleId,
            vehicleNumber: message.data.vehicleNumber,
            latitude: message.data.latitude,
            longitude: message.data.longitude,
            speedKmh: message.data.speedKmh,
            heading: message.data.heading,
            timestamp: message.data.timestamp,
            circuitId: message.data.circuitId ?? null,
            circuitName: message.data.circuitName ?? null,
            confidenceLevel: message.data.confidenceLevel ?? 0,
            associationStatus: message.data.associationStatus ?? 'UNASSOCIATED',
            gpsStatus: 'ONLINE',
            // Télémétrie tracker (§5) — transmise si le boîtier la fournit.
            imei: message.data.imei ?? null,
            altitude: message.data.altitude ?? null,
            ignition: message.data.ignition ?? null,
            mainPowerVoltage: message.data.mainPowerVoltage ?? null,
            backupBatteryPercentage: message.data.backupBatteryPercentage ?? null,
            satellites: message.data.satellites ?? null,
            hdop: message.data.hdop ?? null,
            panicButtonPressed: message.data.panicButtonPressed ?? null,
            doorsOpen: message.data.doorsOpen ?? null,
          }))
        }

        if (message.type === 'ALERT') {
          dispatch(addAlert({
            id: message.alertId,
            type: message.alertType,
            severity: message.severity,
            message: message.message,
            vehicleId: message.vehicleId ?? null,
            status: 'OPEN',
            createdAt: new Date().toISOString(),
          }))
          if (message.severity === 'CRITICAL' || message.severity === 'HIGH') {
            toast.error(`🚨 ${message.message}`, { duration: 5000 })
          }
        }

        // Démo temps réel : état complet du circuit diffusé par le conducteur
        if (message.type === 'DEMO_STATE') {
          dispatch(setDemoState(message.state as DemoState))
        }

        // Statut GPS du bus (Phase 4) : ONLINE / GPS_LOST / OFFLINE.
        if (message.type === 'DRIVER_EVENT' && message.busId && message.event) {
          dispatch(setGpsStatus({ vehicleId: message.busId, gpsStatus: message.event }))
          const me = store.getState().auth.user
          const isSupervisor = me?.role === 'ADMIN' || me?.role === 'MANAGER' || me?.role === 'DISPATCHER'
          if (isSupervisor && message.event === 'GPS_LOST') {
            toast.error(`📡 Signal GPS perdu — ${message.busId}`, { duration: 6000 })
          }
        }

        // Notifications ciblées : le parent concerné par son arrêt choisi (ou ses
        // enfants), l'arrivée/le départ de l'école et les imprévus. Les
        // superviseurs voient tout.
        if (message.type === 'DEMO_EVENT') {
          if (eventConcernsUser(message)) {
            dispatch(setParentMessage(message.message))
            switch (message.kind) {
              case 'STARTED':
                toast(`🚌 ${message.message}`, { duration: 6000 })
                break
              case 'APPROACHING':
                toast(`🔔 ${message.message}`, { duration: 5000 })
                break
              case 'BOARDING':
                dispatch(setBoarded(true))
                toast.success(`🧒 ${message.message}`, { duration: 6000 })
                break
              case 'ARRIVED':
                toast.success(`🏫 ${message.message}`, { duration: 5000 })
                break
              case 'ARRIVED_SCHOOL':
                toast.success(`🎒 ${message.message}`, { duration: 6000 })
                break
              case 'DEPARTED_SCHOOL':
                dispatch(setBoarded(true))
                toast(`🚌 ${message.message}`, { duration: 6000 })
                break
              case 'INCIDENT':
                toast.error(`🚨 ${message.message}`, { duration: 9000 })
                break
              default:
                break
            }
          }
        }
      } catch (e) {
        console.error('Erreur parsing message WebSocket:', e)
      }
    }

    ws.onclose = () => {
      wsRef.current = null
      if (activeWs === ws) activeWs = null
      const delay = backoffRef.current.next()
      console.log(`WebSocket déconnecté, reconnexion dans ${delay} ms…`)
      reconnectTimeoutRef.current = setTimeout(connect, delay)
    }

    ws.onerror = (error) => {
      console.error('Erreur WebSocket:', error)
    }
  }, [dispatch])

  useEffect(() => {
    connect()
    return () => {
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current)
      wsRef.current?.close()
    }
  }, [connect])

  const subscribeToVehicle = useCallback((vehicleId: string) => {
    wsRef.current?.send(JSON.stringify({ type: 'SUBSCRIBE_VEHICLE', vehicleId }))
  }, [])

  return { subscribeToVehicle }
}
